/**
 * 묶음 위임 **그룹 선택 게이트** — 확장 전 placeholder로 들어온 외부 담당자가 작성 폼보다
 * 먼저 만나는 화면(§6 ③, 결정 2026-09-06 「구현 확정 보강」).
 *
 * ⚠️ **작성보다 앞에 있어야 하는 이유가 데이터에 있다**: 그룹 1은 placeholder를 in-place로
 * 채우는데(토큰 앵커·URL 안정), 그 채움이 템플릿을 다시 compose하므로 **확장 전에 저장해 둔
 * 임시 저장분을 덮는다**. 게이트를 지나기 전에 폼을 그리면 담당자가 쓴 것이 조용히 사라진다.
 *
 * ⚠️ 계획 조회를 **버튼 1탭에 묶은 것도 의도**다. `POST :token/split-plan`은 provider 호출이
 * 부수효과(TbmMeta upsert·AI 정제 캐시)를 갖는 경로라(서버 `getPlan` JSDoc), 페이지 로드에
 * 걸면 새로고침이 그대로 부수효과가 된다.
 *
 * 이 컴포넌트는 화면만 그린다 — 조회·확장·전이는 전부 호스트 페이지 소관이다.
 */
import type { ReactNode } from 'react';
import { Button, Card } from '@safetyhub/ui';
import { ReportWriteShell } from '../../../components/reports/ReportWriteShell';

export interface SplitGroupGateScreenProps {
  title: string;
  subtitle: string;
  /** 위임자가 쓴 안내문(draft.message) — 있으면 그대로 인용한다. */
  message?: string | null;
  /** 계획 조회 중 — 버튼 비활성 + 라벨 전환. */
  isLoading?: boolean;
  /** 조회 실패 안내(재시도 가능). 배너로만 띄우고 버튼은 살려 둔다. */
  error?: string;
  onStart: () => void;
  /**
   * 계획이 도착한 뒤 **같은 화면에 펼칠** 그룹 선택 — 있으면 안내 카드와 시작 버튼을 대체한다.
   *
   * 종전에는 이걸 `modals`로 받아 게이트 위에 모달을 덮었다. 그러면 한 흐름이 **게이트 화면 +
   * 모달** 두 겹이 되는데, 게이트 화면은 안내 한 카드뿐이라 그 아래가 통째로 빈다
   * (2026-09-10 사용자 지적: 「굳이 다시 클릭해서 범위 선택을 해야 하나」).
   * 클릭 자체는 남긴다 — 계획 조회가 부수효과를 갖는 경로라 명시적 조작 1회가 그 승인이다
   * (컴포넌트 헤더 ⚠️ 두 번째 절).
   */
  selection?: ReactNode;
}

export function SplitGroupGateScreen({
  title,
  subtitle,
  message,
  isLoading = false,
  error,
  onStart,
  selection,
}: SplitGroupGateScreenProps) {
  return (
    <ReportWriteShell
      title={title}
      subtitle={subtitle}
      error={error}
      maxWidth="max-w-4xl"
      /* 이 화면엔 **앱 상단바가 없다**(무인증 라우트라 `Layout` 밖이다). 그런데 공유 헤더는
         `sticky top-14`로 그 상단바 몫 56px을 비워 두므로, 조금만 스크롤되면 헤더가 56px
         아래에 고정되고 그 위가 빈 띠로 남으면서 **본문이 헤더 밑으로 밀려 들어가 잘려 보인다**
         (2026-09-10 사용자 제보: 스크롤하지 않았는데 카드 제목이 가려짐 — 스크롤 복원 등으로
         조금만 밀려도 발생). 있지도 않은 것에 자리를 비워 둘 이유가 없으니 0으로 고정한다. */
      stickyTopOffset={0}
    >
      {/* 선택이 펴지면 목록이 길어 위에서부터 채우고, 안내 상태는 카드 하나뿐이라 위에 붙이면
          아래가 통째로 빈다 — 그 상태에서만 가운데로 세우고 폭을 좁힌다(2026-09-10). */}
      <div
        className={
          selection
            ? 'max-w-4xl mx-auto px-4 py-6'
            : 'max-w-2xl mx-auto px-4 py-6 min-h-[60vh] flex flex-col justify-center'
        }
      >
        {selection ?? (
          <Card padding="lg" elevation="none">
            <h2 className="text-sm font-semibold text-ink-800">작성할 문서를 먼저 나눠주세요</h2>
            <p className="mt-2 text-sm text-ink-600">
              이 점검은 여러 건의 문서로 나누어 작성합니다. 아래 버튼을 눌러 어떤 묶음으로 나눌지
              먼저 정한 뒤 작성을 시작하세요.
            </p>
            <p className="mt-2 text-xs text-ink-500">
              나눈 뒤에는 문서 구성을 다시 바꿀 수 없으니 확인 후 진행해주세요.
            </p>
            {message && (
              <p className="mt-4 rounded border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-700">
                관리자 메시지: {message}
              </p>
            )}
            {/* CTA를 안내 **바로 아래**에 둔다 — 종전엔 화면 맨 아래 고정바에 있어, 설명은 위에
                있고 행동은 저 멀리 있는 화면이 됐다(그 사이가 통째로 빈 공간이었다). */}
            {/* CTA를 안내 **바로 아래**에 둔다 — 종전엔 화면 맨 아래 고정바에 있어, 설명은 위에
                있고 행동은 저 멀리 있는 화면이 됐다(그 사이가 통째로 빈 공간이었다). */}
            <div className="mt-5">
              <Button variant="primary" onClick={onStart} disabled={isLoading}>
                {isLoading ? '불러오는 중...' : '문서 그룹 선택'}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </ReportWriteShell>
  );
}

export default SplitGroupGateScreen;
